import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import { addProcedureAction as addProcedure } from '../../../../../../services/EncounterService/slice';
import { useGetServicesQuery, useGetRoomsQuery } from '../../../../../../services/ConfigurationService';
import { useServiceAddForm } from './useServiceAddForm';
import type { ProcedureType } from '../../../../../../services/EncounterService/types/encounterTypes';

interface UseServiceAddDialogProps {
  onServiceAdded?: () => void;
}

export const useServiceAddDialog = ({ onServiceAdded }: UseServiceAddDialogProps = {}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  
  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'Cancelled';

  // Get center ID from URL parameters
  const company = searchParams.get('company');
  const center = searchParams.get('center');
  const centerId = company && center ? `${company}-${center}` : '';

  // Fetch services and rooms from configuration API
  const { data: services = [] } = useGetServicesQuery(
    { centerId },
    { skip: !centerId }
  );
  
  const { data: rooms = [] } = useGetRoomsQuery(
    { centerId },
    { skip: !centerId }
  );

  // Dialog open state
  const [open, setOpen] = useState(false);

  // Form state
  const formState = useServiceAddForm();

  const handleOpen = useCallback(() => {
    formState.resetForm();
    setOpen(true);
  }, [formState]);

  const handleClose = useCallback(() => {
    setOpen(false);
    formState.resetForm();
  }, [formState]);

  const handleSubmit = useCallback(() => {
    if (!formState.validateForm()) return;

    // Create new procedure
    const newProcedure: ProcedureType = {
      id: `temp-${Date.now()}`,
      priority: formState.formData.priority,
      modifier: formState.formData.modifier || undefined,
      description: formState.formData.description || undefined,
      isPrimary: formState.formData.isPrimary,
      procedureStart: formState.formData.procedureStart || undefined,
      procedureEnd: formState.formData.procedureEnd || undefined,
      procedureDuration: formState.formData.procedureDuration ? Number(formState.formData.procedureDuration) : undefined,
      procedureTimeUnits: formState.formData.procedureTimeUnits || undefined,
      serviceCode: formState.formData.serviceCode || undefined,
      serviceDescription: formState.formData.serviceDescription || undefined,
      room: formState.formData.room || undefined,
    };

    dispatch(addProcedure(newProcedure));
    onServiceAdded?.();
    handleClose();
  }, [dispatch, formState, handleClose, onServiceAdded]);

  return {
    open,
    services,
    rooms,
    isEncounterCancelled,
    handleOpen,
    handleClose,
    handleSubmit,
    ...formState,
  };
};
